import React from 'react';
import { getGreeting } from '../../utils/greeting';
import QuickActionChips from './QuickActionChips';

interface ChatHomeStateProps {
  fullName: string | null;
  username?: string | null;
  onPickAction: (scaffold: string) => void;
  /** Disable quick actions while a send/load is in flight. */
  disabled?: boolean;
}

/** AIR brand "slashes" (/// design element from the brand guidelines) — the
 *  on-brand accent above the greeting (ties to the slash in the AIRAssist logo). */
const BrandSlashes: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 52 32" aria-hidden="true" className={className} fill="currentColor">
    <polygon points="2,28 9,28 19,4 12,4" />
    <polygon points="16,28 23,28 33,4 26,4" />
    <polygon points="30,28 37,28 47,4 40,4" />
  </svg>
);

/**
 * The personalized chat home/empty state: a navy spark, a warm serif time-of-day
 * greeting, and the 2x2 grid of HVAC quick actions. The caller renders this only
 * when the conversation is empty. Lives inside the Chat scroll region (R55) and
 * centers vertically so everything stays above the fold on mobile (R63).
 */
const ChatHomeState: React.FC<ChatHomeStateProps> = ({ fullName, username, onPickAction, disabled }) => {
  const { headline } = getGreeting(fullName, new Date(), username);

  return (
    <div className="flex h-full flex-col items-center justify-center px-2">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <BrandSlashes className="mb-4 h-7 w-12 text-air-navy dark:text-air-skytext" />
          {/* Greeting in the AIR brand serif (Cambria) — warm, on-brand. */}
          <h2 className="font-serif text-3xl font-semibold leading-tight tracking-tight text-air-navy dark:text-air-skytext sm:text-4xl">
            {headline}
          </h2>
        </div>
        <QuickActionChips onPick={onPickAction} disabled={disabled} />
      </div>
    </div>
  );
};

export default ChatHomeState;
